import type { AuthUser } from '@/shared/store/auth-store'
import type { AdminScope } from '@/shared/constants/admin-scope'
import { AdminPathSegment, AdminScopeType } from '@/shared/constants/admin-scope'
import { AuthRole } from '@/shared/constants/auth'

export interface LoginRouteContext {
  tenantSlug: string | null
  successPath: string
}

// Danh sách các slug bị giữ lại cho hệ thống, không được phép dùng làm tenant slug.
export const RESERVED_TENANT_SLUGS = [
  AdminPathSegment.Super,
  AdminPathSegment.Admin,
  AdminPathSegment.Login,
  'logout',
  'signup',
  'api',
  'static',
  'assets',
  'public',
  'system',
  'health',
  'status',
  '_next',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml',
] as const

const RESERVED_TENANT_SLUG_SET = new Set<string>(RESERVED_TENANT_SLUGS)

export const TENANT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/

/**
 * Chuẩn hóa chuỗi thành tenant slug (lowercase, bỏ ký tự lạ, gạch nối).
 *
 * @param value - Chuỗi gốc (có thể chứa khoảng trắng, chữ hoa, ký tự không hợp lệ).
 * @returns Slug đã chuẩn hóa; có thể rỗng nếu input không còn ký tự hợp lệ sau khi xử lý.
 */
export function normalizeTenantSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Kiểm tra slug có nằm trong danh sách reserved (không được dùng làm tenant) hay không.
 *
 * @param value - Slug cần kiểm tra (so sánh không phân biệt hoa thường).
 * @returns `true` nếu slug bị giữ cho hệ thống (vd: `super`, `login`, ...).
 */
export function isReservedTenantSlug(value: string): boolean {
  return RESERVED_TENANT_SLUG_SET.has(value.toLowerCase())
}

/**
 * Slug hợp lệ khi khớp {@link TENANT_SLUG_PATTERN} và không phải reserved.
 *
 * @param value - Slug tenant (đã chuẩn hóa hoặc gần đúng format).
 * @returns `true` nếu có thể dùng làm tenant slug trong URL.
 */
export function isValidTenantSlug(value: string): boolean {
  return TENANT_SLUG_PATTERN.test(value) && !isReservedTenantSlug(value)
}

/**
 * So khớp role super admin với giá trị backend (`AuthRole.SuperAdmin`).
 *
 * @param role - Role từ session/API; có thể thiếu.
 * @returns `true` nếu user là super admin.
 */
export function isSuperAdminRole(role?: string | null): boolean {
  return role === AuthRole.SuperAdmin
}

/**
 * So khớp role tenant admin với giá trị backend (`AuthRole.Admin`).
 *
 * @param role - Role từ session/API; có thể thiếu.
 * @returns `true` nếu user là admin của một tenant.
 */
export function isTenantAdminRole(role?: string | null): boolean {
  return role === AuthRole.TenantAdmin
}

/**
 * Đường dẫn đăng nhập theo tenant: `/{slug}/login`.
 *
 * @param tenantSlug - Slug tenant trong URL.
 * @returns Path đầy đủ dùng cho `navigate` / link.
 */
export function getTenantLoginPath(tenantSlug: string): string {
  return `/${tenantSlug}/${AdminPathSegment.Login}`
}

/**
 * Đường dẫn đăng nhập super admin: `/super/login`.
 *
 * @returns Path login cho scope super.
 */
export function getSuperLoginPath(): string {
  return `/${AdminPathSegment.Super}/${AdminPathSegment.Login}`
}

/**
 * Trang chủ admin khi scope super: `/super`.
 *
 * @returns Base path của khu vực super admin.
 */
export function getSuperAdminHomePath(): string {
  return `/${AdminPathSegment.Super}`
}

/**
 * Trang chủ admin theo tenant: `/{slug}/admin`.
 *
 * @param tenantSlug - Slug tenant trong URL.
 * @returns Base path của khu vực admin tenant đó.
 */
export function getTenantAdminHomePath(tenantSlug: string): string {
  return `/${tenantSlug}/${AdminPathSegment.Admin}`
}

/**
 * Suy luận scope admin từ `pathname` hiện tại.
 *
 * - `/super/...` → super.
 * - `/{tenant}/admin/...` hoặc `/{tenant}/login/...` (segment đầu không reserved) → tenant.
 *
 * @param pathname - Ví dụ `location.pathname` từ router.
 * @returns `AdminScope` nếu nhận diện được; ngược lại `null`.
 */
export function getAdminScopeFromPath(pathname: string): AdminScope | null {
  const [, firstSegment, secondSegment] = pathname.split('/')
  if (!firstSegment) return null
  if (firstSegment === AdminPathSegment.Super) return { type: AdminScopeType.Super }
  if (
    (secondSegment === AdminPathSegment.Admin || secondSegment === AdminPathSegment.Login) &&
    !isReservedTenantSlug(firstSegment)
  ) {
    return { type: AdminScopeType.Tenant, tenantSlug: firstSegment }
  }
  return null
}

/**
 * Trích tenant slug từ URL dạng `/{tenantSlug}/login` (chỉ trong môi trường có `window`).
 *
 * @param pathname - Path cần parse.
 * @returns Slug tenant hoặc `null` nếu không phải pattern login tenant hợp lệ.
 */
export function getTenantSlugFromLoginPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length < 2 || segments[segments.length - 1] !== AdminPathSegment.Login) {
    return null
  }

  const [tenantSlug] = segments
  if (
    !tenantSlug ||
    tenantSlug === AdminPathSegment.Auth ||
    tenantSlug === AdminPathSegment.Super
  ) {
    return null
  }

  return tenantSlug
}

/**
 * Context cho màn login: slug tenant (nếu có) và path redirect sau khi đăng nhập thành công.
 *
 * Thứ tự ưu tiên `tenantSlug`: `routeTenantSlug` → scope từ pathname → parse từ login path.
 *
 * @param pathname - Path hiện tại (vd từ router).
 * @param routeTenantSlug - Slug từ route param (ưu tiên cao nhất nếu có).
 * @returns `{ tenantSlug, successPath }` — `successPath` là home admin tương ứng.
 */
export function getLoginRouteContext(
  pathname: string,
  routeTenantSlug?: string,
): LoginRouteContext {
  const scope = getAdminScopeFromPath(pathname)
  const tenantSlug =
    routeTenantSlug ??
    (scope?.type === AdminScopeType.Tenant ? scope.tenantSlug : null) ??
    getTenantSlugFromLoginPath(pathname)

  return {
    tenantSlug,
    successPath: tenantSlug
      ? getTenantAdminHomePath(tenantSlug)
      : scope
        ? getAdminHomePathForScope(scope)
        : getSuperAdminHomePath(),
  }
}

/**
 * Suy ra admin scope từ user đã đăng nhập.
 *
 * Super admin → luôn scope super. Tenant admin → cần slug: ưu tiên `activeTenantSlug`, sau đó profile.
 *
 * @param user - User từ store/session; `null` nếu chưa đăng nhập.
 * @param activeTenantSlug - Slug tenant đang active (vd từ URL), optional.
 * @returns Scope tương ứng hoặc `null` nếu không suy ra được.
 */
export function getAdminScopeForUser(
  user: AuthUser | null,
  activeTenantSlug?: string | null,
): AdminScope | null {
  if (!user) return null
  if (isSuperAdminRole(user.role)) return { type: AdminScopeType.Super }
  const tenantSlug = activeTenantSlug ?? user.tenantSlug
  if (!tenantSlug) return null
  return { type: AdminScopeType.Tenant, tenantSlug }
}

/**
 * Home path admin theo scope (`/super` hoặc `/{slug}/admin`).
 *
 * @param scope - Scope super hoặc tenant.
 * @returns Path home tương ứng.
 */
export function getAdminHomePathForScope(scope: AdminScope): string {
  return scope.type === AdminScopeType.Super
    ? getSuperAdminHomePath()
    : getTenantAdminHomePath(scope.tenantSlug)
}

/**
 * Path login phù hợp với scope.
 *
 * @param scope - Scope hiện tại; `null` hoặc super → `/super/login`, tenant → `/{slug}/login`.
 * @returns Path đăng nhập.
 */
export function getLoginPathForScope(scope: AdminScope | null): string {
  if (!scope || scope.type === AdminScopeType.Super) return getSuperLoginPath()
  return getTenantLoginPath(scope.tenantSlug)
}

/**
 * Ghép base theo scope với route module nội bộ (sidebar dùng `/dashboard`, `/tenant`, ...).
 *
 * @param scope - Super hoặc tenant.
 * @param route - Segment module nội bộ; `/dashboard` được coi là rỗng (chỉ còn base).
 * @returns Path đầy đủ trong URL thật (scoped).
 */
export function buildScopedAdminPath(scope: AdminScope, route: string): string {
  const normalizedRoute = route === '/dashboard' ? '' : route.startsWith('/') ? route : `/${route}`
  const basePath =
    scope.type === AdminScopeType.Super
      ? getSuperAdminHomePath()
      : getTenantAdminHomePath(scope.tenantSlug)
  return `${basePath}${normalizedRoute}`
}

/**
 * Đảo ngược {@link buildScopedAdminPath}: từ URL scoped (có prefix `/super` hoặc `/{slug}/admin`)
 * về route module nội bộ dùng cho sidebar / so khớp menu.
 *
 * @param pathname - Path đầy đủ hiện tại.
 * @returns Route module nội bộ; nếu chỉ còn base scope thì `/dashboard`; nếu không nhận diện scope thì trả về `pathname` gốc.
 *
 * @example Super admin
 * ```ts
 * getAdminModuleRouteFromPath('/super')           // '/dashboard'
 * getAdminModuleRouteFromPath('/super/tenant')    // '/tenant'
 * getAdminModuleRouteFromPath('/super/tours')     // '/tours'
 * ```
 *
 * @example Tenant admin (slug `acme`)
 * ```ts
 * getAdminModuleRouteFromPath('/acme/admin')      // '/dashboard'
 * getAdminModuleRouteFromPath('/acme/admin/pois') // '/pois'
 * ```
 */
export function getAdminModuleRouteFromPath(pathname: string): string {
  const scope = getAdminScopeFromPath(pathname)
  if (!scope) return pathname

  if (scope.type === AdminScopeType.Super) {
    const rest = pathname.slice(getSuperAdminHomePath().length)
    return rest || '/dashboard'
  }

  const tenantBase = getTenantAdminHomePath(scope.tenantSlug)
  const rest = pathname.slice(tenantBase.length)
  return rest || '/dashboard'
}
